import { Dialog, Transition } from "@headlessui/react";
import { FC, Fragment, PropsWithChildren } from "react";
import { classNames, wrapClick } from "utils";

import Loader from "./loader";
import { XMarkIcon } from "@heroicons/react/24/outline";
import config from "config";

interface DrawerProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (val: boolean) => void;
  renderActions?: () => JSX.Element;
  hideActions?: boolean;
  hidePadding?: boolean;
  size?:
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"; // Added smaller sizes typical for drawers
  loading?: boolean;
}

const sizeMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
};

const Drawer: FC<PropsWithChildren<DrawerProps>> = ({
  open,
  setOpen,
  children,
  renderActions,
  title,
  description,
  hideActions,
  hidePadding,
  size = "md", // Default to a more common drawer size
  loading,
}) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className={classNames(
          "",
          "relative z-50"
        )} // Changed from fixed inset-0
        onClose={setOpen} // Allow closing by clicking outside
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300" // Adjusted duration
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-200" // Adjusted duration
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 z-50 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300" // Adjusted duration
                enterFrom="translate-x-full" // Slide in from right
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200" // Adjusted duration
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full" // Slide out to right
              >
                <Dialog.Panel
                  className={classNames(
                    "pointer-events-auto w-screen",
                    sizeMap[size]
                  )}
                >
                  <div className="flex h-full flex-col overflow-hidden z-50 shadow-xl">
                    {/* Header */}
                    <div className="bg-primary-500 z-50 px-4 py-6 sm:px-6">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-lg font-medium text-white">
                          {title}
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="rounded-md bg-primary-200 text-tertiary-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => setOpen(false)}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      {description && (
                        <div className="mt-1">
                          <p className="text-sm text-tertiary">{description}</p>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div
                      className={classNames(
                        "relative flex-1 bg-white overflow-y-scroll",
                        hidePadding ? "" : "px-4 py-6 sm:px-6" // Apply padding unless hidden
                      )}
                    >
                      {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader />
                        </div>
                      ) : (
                        children
                      )}
                    </div>

                    {/* Actions */}
                    {!hideActions && !loading && (
                      <div className="flex flex-shrink-0 justify-end border-t border-background-500 px-4 py-4">
                        <button
                          type="button"
                          className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                          onClick={wrapClick(() => setOpen(false))}
                        >
                          Close
                        </button>
                        <>{renderActions?.()}</>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition >
  );
};

export default Drawer;
